import { LoanProductService } from '@/modules/global-settings/loan-origination/services/loan-policies/loan-product.service';
import { make } from 'vuex-pathify';
import Logger from '@/lib/logger';
import { LoanTypeService } from '../../services/loan-policies/loan-type.service.js';
import {
  assertUniqueLoanProductName,
  normalizeLoanProductNameForSave
} from '@/modules/global-settings/loan-origination/utils/loan-product-name';
// Instantiate the service
const loanProductService = new LoanProductService();
const loanTypeService = new LoanTypeService();
// Define the state
export const state = {
  loanProducts: [],
  loanProductClassification: ['Personal', 'Business', 'Both']
};
// Define the mutations
export const mutations = {
  ...make.mutations(state)
};
// Define the actions
export const actions = {
  /**
   * Fetch all loan products
   * @async
   * @param {object} context - The store context
   * @returns {Array} - The loan products
   * @private This will update the state with the fetched loan products
   */
  getLoanProducts: async ({ commit }) => {
    try {
      const { data } = await loanProductService.getLoanProducts();
      const promises = []
      data.forEach( lp => {
        promises.push(loanTypeService.fetchLoanTypeById(lp.loanTypeRef));
      });
      const results = await Promise.all(promises)
      results.forEach((result, i) => data[i].loanType = result.data.loanTypeName)
      commit('SET_LOAN_PRODUCTS', data);
      return data;
    } catch (error) {
      Logger.error('getting loan product', error);
      throw error;
    }
  },
  /**
   * Fetch a loan product
   * @async
   * @param {object} context - The store context
   * @param {object} id - The loan product id
   * @returns {object} - The loan product
   */
  getLoanProduct: async (_, id) => {
    try {
      const { data } = await loanProductService.getLoanProduct(id);
      return data;
    } catch (error) {
      Logger.error('getting loan product', error);
      throw error;
    }
  },
  /**
   * Create a loan product
   * @async
   * @param {object} context - The store context
   * @param {object} payload - The loan product payload
   * @returns {object} - The created loan product
   * @private This will refetch the loan products
   */
  createLoanProduct: async ({ dispatch, state }, payload) => {
    try {
      const normalizedPayload = { ...payload };
      normalizedPayload.loanProductName = normalizeLoanProductNameForSave(
        normalizedPayload.loanProductName
      );
      assertUniqueLoanProductName(state.loanProducts, normalizedPayload);
      const { data } = await loanProductService.createLoanProduct(
        normalizedPayload
      );
      dispatch('getLoanProducts');
      return data;
    } catch (error) {
      Logger.error('creating loan product', error);
      throw error;
    }
  },
  /**
   * Update a loan product
   * @async
   * @param {object} context - The store context
   * @param {object} payload - The loan product payload
   * @returns {void}
   * @private This will refetch the loan products
   */
  updateLoanProduct: async ({ dispatch, state }, payload) => {
    try {
      const normalizedPayload = { ...payload };

      if (
        Object.prototype.hasOwnProperty.call(normalizedPayload, 'loanProductName')
      ) {
        normalizedPayload.loanProductName = normalizeLoanProductNameForSave(
          normalizedPayload.loanProductName
        );
        assertUniqueLoanProductName(state.loanProducts, normalizedPayload);
      }

      await loanProductService.updateLoanProduct(normalizedPayload);
      dispatch('getLoanProducts');
    } catch (error) {
      Logger.error('updating loan product', error);
      throw error;
    }
  }
};
// Define the getters
export const getters = make.getters(state);
